import React, {useEffect, useRef} from 'react';
import {Animated, Easing, StyleProp, ViewStyle} from 'react-native';

// Card/section entrance: fade in + rise 16px (DEV_HANDOFF_SPEC §5 "Card
// entrance", 0.45s ease-out, staggered via `delay`). Children always render
// immediately -- only opacity/translate animate -- so content is present for
// tests and first paint. `axis: 'x'` gives the row-entrance slide (spec:
// translateX -12 -> 0).
type Props = {
  delay?: number;
  distance?: number;
  duration?: number;
  axis?: 'y' | 'x';
  style?: StyleProp<ViewStyle>;
  children: React.ReactNode;
};

export default function FadeInUp({
  delay = 0,
  distance = 16,
  duration = 450,
  axis = 'y',
  style,
  children,
}: Props) {
  const v = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const anim = Animated.timing(v, {
      toValue: 1,
      duration,
      delay,
      easing: Easing.out(Easing.cubic),
      // react-native-web has no native driver.
      useNativeDriver: false,
    });
    anim.start();
    return () => anim.stop();
  }, [v, duration, delay]);
  const offset = v.interpolate({
    inputRange: [0, 1],
    outputRange: [axis === 'x' ? -12 : distance, 0],
  });
  return (
    <Animated.View
      style={[
        {
          opacity: v,
          transform: [axis === 'x' ? {translateX: offset} : {translateY: offset}],
        },
        style,
      ]}>
      {children}
    </Animated.View>
  );
}
